import React from 'react'
import PropTypes from 'prop-types'
import { navigate } from 'gatsby'
import { Spacing50, Spacing100 } from 'styles/sharedStyle'
import NestedFadeInWrapper from 'components/FadeInWrapper/NestedWrapper'

import {
  checkExternalUrl,
  normalizeExternalUrl,
  normalizeInternalUrl,
} from 'utils/urlUtils'

import AccordionItem from './AccordionItem'
import {
  ContentContainer,
  Content,
  AccordionCta,
  Heading,
  List,
  Wrapper,
  ImageContainer,
  Image,
  NoImgContainer,
} from './Accordion.style'

import { fixAudiences } from '../../utils/audiences'

const delayAccCtrl = (start = 0, incrementAmount = 0) => {
  let value = start

  return () => {
    value += incrementAmount
    return value
  }
}

class Accordion extends React.Component {
  constructor(props) {
    super(props)

    this.id =
      props.name ||
      `accordion_${Math.random()
        .toString(36)
        .substring(7)}`

    this.state = {
      current: null,
    }

    this.toggle = (index) => {
      this.setState(({ current }) => ({
        current: current === index ? null : index,
      }))
    }

    this.onCtaPress = () => {
      const { ctaUrl, villageUrl, nodeLocale } = props

      navigate(
        // @ts-ignore
        checkExternalUrl(ctaUrl)
          ? normalizeExternalUrl(ctaUrl)
          : `/${villageUrl}/${nodeLocale}${normalizeInternalUrl(ctaUrl)}`
      )
    }
  }

  render() {
    const {
      bgImage,
      bgPosition = 'Right',
      ctaLabel = '',
      ctaUrl = '',
      audiences,
      children,
      heading,
      items,
      spacing,
      villageSlug,
      nodeLocale,
      alternateVersion,
      startAtDelay,
      delayFactor,
      animated,
      villageUrl,
    } = this.props
    const { current } = this.state
    const hasAudiences = !!audiences
    const getAnimDelay = delayAccCtrl(startAtDelay, delayFactor)

    if (bgImage) {
      return (
        <Wrapper
          alternateVersion={alternateVersion}
          hasAudience={hasAudiences}
          className={fixAudiences(audiences)}
          noBorderTop>
          {heading && <Heading>{heading}</Heading>}
          <ContentContainer data-direction={bgPosition}>
            <Content>
              <List>
                {items &&
                  items.map((item, i) => (
                    <AccordionItem
                      active={current === i}
                      toggle={this.toggle}
                      index={i}
                      name={this.id}
                      {...item}
                      key={`accordion-item__${i.toString()}`}
                      villageSlug={villageSlug}
                      villageUrl={villageUrl}
                      nodeLocale={nodeLocale}
                      alternateVersion={alternateVersion}>
                      {!!children &&
                        (children.length === items.length ||
                          (typeof children === 'object' &&
                            items.length === 1)) &&
                        children}
                    </AccordionItem>
                  ))}
              </List>
              {!!ctaLabel && !!ctaUrl && (
                // @ts-ignore
                <AccordionCta onClick={this.onCtaPress}>
                  {ctaLabel}
                </AccordionCta>
              )}
            </Content>
            <ImageContainer>
              <Image
                image={bgImage?.portrait?.gatsbyImageData}
                alt={bgImage?.altText || 'Accordion'}
              />
            </ImageContainer>
          </ContentContainer>
        </Wrapper>
      )
    }

    return (
      <NoImgContainer>
        <Wrapper
          alternateVersion={alternateVersion}
          hasAudience={hasAudiences}
          className={fixAudiences(audiences)}>
          {spacing && <Spacing100 />}
          {heading && (
            <>
              <Heading>{heading}</Heading>
              {spacing && <Spacing50 />}
            </>
          )}
          <List alternateVersion={alternateVersion}>
            {items &&
              items.map((item, i) => (
                <NestedFadeInWrapper
                  enabled={animated}
                  delay={i === 0 ? startAtDelay : getAnimDelay()}>
                  <AccordionItem
                    active={current === i}
                    toggle={this.toggle}
                    index={i}
                    name={this.id}
                    {...item}
                    key={`accordion-item__${i.toString()}`}
                    villageSlug={villageSlug}
                    villageUrl={villageUrl}
                    nodeLocale={nodeLocale}
                    alternateVersion={alternateVersion}>
                    {!!children &&
                      (children.length === items.length ||
                        (typeof children === 'object' && items.length === 1)) &&
                      children}
                  </AccordionItem>
                </NestedFadeInWrapper>
              ))}
          </List>
        </Wrapper>
        {spacing && <Spacing50 />}
      </NoImgContainer>
    )
  }
}

Accordion.propTypes = {
  audiences: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  heading: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object])),
  name: PropTypes.string,
  spacing: PropTypes.bool,
  villageSlug: PropTypes.string,
  nodeLocale: PropTypes.string,
  alternateVersion: PropTypes.bool,
  bgImage: PropTypes.oneOfType([PropTypes.object]),
  bgPosition: PropTypes.string,
  ctaLabel: PropTypes.string,
  ctaUrl: PropTypes.string,
  startAtDelay: PropTypes.number,
  delayFactor: PropTypes.number,
  animated: PropTypes.bool,
  villageUrl: PropTypes.string,
}

Accordion.defaultProps = {
  audiences: '',
  children: null,
  heading: '',
  items: [],
  name: '',
  spacing: true,
  villageSlug: '',
  nodeLocale: '',
  alternateVersion: false,
  bgImage: null,
  bgPosition: 'Right',
  ctaLabel: '',
  ctaUrl: '',
  startAtDelay: 0,
  delayFactor: 0,
  animated: false,
  villageUrl: '',
}

export default Accordion
